/* eslint-disable */
import { useEffect, useState } from 'react';
import { IntegrationInfo } from '../../store/config/types/integration.types';
import { IntegrationService } from '../../services/integration.service';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Typography,
} from '@mui/material';
import parser from 'cron-parser';
import moment from 'moment-timezone';
import { dateFormat } from '../AIAgents/AIAgents';

interface IntegrationInfoDialogProps {
  name?: string;
  timezone?: string;
  onClose?: () => void;
}

export default function IntegrationInfoDialog({ name, timezone, onClose }: IntegrationInfoDialogProps) {
  const [info, setInfo] = useState<IntegrationInfo[]>();
  const [fetching, setFetching] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [open, setOpen] = useState(false);
  const [autoCloseRemaining, setAutoCloseRemaining] = useState(10);

  useEffect(() => {
    setFetching(true);
    setErrorMessage([]);
    if (name) {
      setOpen(true);
      setAutoCloseRemaining(10);
      IntegrationService.getInfo(name)
        .then((data) => {
          setInfo(data);
        })
        .catch((err) => {
          setErrorMessage([err.message]);
        })
        .finally(() => {
          setFetching(false);
        });
    } else {
      setOpen(false);
    }
  }, [name]);

  // Automatically close dialog after 10 seconds when syncing starts, with countdown
  useEffect(() => {
    if (syncing) {
      setAutoCloseRemaining(10);
      const timer = setTimeout(() => {
        if (onClose) onClose();
        setOpen(false);
      }, 10000);

      const interval = setInterval(() => {
        setAutoCloseRemaining((prev) => (prev > 1 ? prev - 1 : 1));
      }, 1000);

      return () => {
        clearTimeout(timer);
        clearInterval(interval);
      };
    }
  }, [syncing, onClose]);

  function hancleSyncNowClick(shipperEscId: number) {
    if (name) {
      setSyncing(true);
      setErrorMessage([]);
      IntegrationService.syncNow(name, shipperEscId)
        .then((data) => {
          setInfo(data);
          setSyncing(false);
        })
        .catch((err) => {
          setErrorMessage([err.message]);
        })
        .finally(() => {
          setSyncing(false);
        });
    }
  }
  const schedule = parser;
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Integration Info</DialogTitle>
      <DialogContent>
        {fetching ? (
          <Grid container>
            <Typography variant="caption" color="skyblue">
              <CircularProgress size={10} /> Fetching
            </Typography>
          </Grid>
        ) : info && info.length > 0 ? (
          <>
            {info.map((item) => {
              let interval = null;
              let nextSchedule = null;
              try {
                if (item.scheduleSettings.sync.trim()) {
                  interval = parser.parseExpression(item.scheduleSettings.sync.trim(), { utc: true });
                  nextSchedule = interval.next().toDate();
                }
              } catch {}
              return (
                <Grid container direction="column" spacing={2} key={item.shipperEscId} sx={{ mb: 2 }}>
                  <Grid item>
                    <Typography variant="subtitle1">{item.displayName}</Typography>
                  </Grid>
                  <Grid item container>
                    <Grid item xs={4}>
                      <strong>Last Sync Date:</strong>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography variant="caption">
                        {item.lastSyncDate ? moment(item.lastSyncDate).format(dateFormat) : '-'}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid item container>
                    <Grid item xs={4}>
                      <strong>Next Sync:</strong>
                    </Grid>
                    <Grid item xs={8}>
                      <Grid container justifyContent="space-between">
                        {syncing || item.syncing ? (
                          <Typography variant="caption" color="skyblue">
                            <CircularProgress size={10} /> Syncing
                          </Typography>
                        ) : (
                          <>
                            <Typography variant="caption">
                              {nextSchedule ? moment(nextSchedule).format(dateFormat) : 'Never'}
                            </Typography>
                            <Link style={{ cursor: 'pointer' }} onClick={() => hancleSyncNowClick(item.shipperEscId)}>
                              Sync now
                            </Link>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              );
            })}
          </>
        ) : (
          <Grid container>
            <span>No integration info available.</span>
          </Grid>
        )}
        {errorMessage.length > 0 ? (
          <Grid container>
            {/* Display error messages */}
            {errorMessage.map((msg, idx) => (
              <span key={idx} style={{ color: 'red' }}>
                {msg}
              </span>
            ))}
          </Grid>
        ) : (
          syncing && (
            <Alert variant="outlined" color="success">
              Sync started. This dialog will close automatically in {autoCloseRemaining} seconds.
            </Alert>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
